import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Eye, Heart, Shield, Award, CheckCircle2 } from 'lucide-react';
import { vision, mission, values, companyInfo } from '../mock';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">About Us</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">About Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {companyInfo.description}
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Established in {companyInfo.established}, Green Source Fuels emerged as a Level 1 B-BBEE owned company with a clear vision: to become the preferred wholesale diesel supplier in the SADC region.
                </p>
                <p>
                  From our first contract valued at R25 Million, we've grown into one of South Africa's top established diesel wholesalers, directly loading under major brands like Shell, Engen, and BP.
                </p>
                <p>
                  Today, we serve the logistics, mining, and farming industries across Southern Africa, holding a valid and unblemished wholesale license from the Department of Energy of the Republic of South Africa.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardHeader>
                  <Award className="h-10 w-10 text-emerald-600 mb-2" />
                  <CardTitle>Level 1 B-BBEE</CardTitle>
                  <p className="text-sm text-gray-600">Certified empowerment credentials</p>
                </CardHeader>
              </Card>
              
              <Card className="bg-teal-50 border-teal-200">
                <CardHeader>
                  <Shield className="h-10 w-10 text-teal-600 mb-2" />
                  <CardTitle>Licensed</CardTitle>
                  <p className="text-sm text-gray-600">Valid wholesale certificate</p>
                </CardHeader>
              </Card>
              
              <Card className="bg-green-50 border-green-200">
                <CardHeader>
                  <CheckCircle2 className="h-10 w-10 text-green-600 mb-2" />
                  <CardTitle>Quality Assured</CardTitle>
                  <p className="text-sm text-gray-600">Direct loading from majors</p>
                </CardHeader>
              </Card>
              
              <Card className="bg-cyan-50 border-cyan-200">
                <CardHeader>
                  <Target className="h-10 w-10 text-cyan-600 mb-2" />
                  <CardTitle>Regional Reach</CardTitle>
                  <p className="text-sm text-gray-600">SADC-wide operations</p>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-emerald-200 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="h-14 w-14 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                  <Eye className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-3xl mb-4">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-lg leading-relaxed">{vision}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="h-14 w-14 rounded-lg bg-teal-100 flex items-center justify-center mb-4">
                  <Target className="h-8 w-8 text-teal-600" />
                </div>
                <CardTitle className="text-3xl mb-4">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-lg leading-relaxed">{mission}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-base px-4 py-1">Our Values</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Drives Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values guide every decision and interaction
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 text-center">
                <CardHeader>
                  <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-emerald-600" />
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Ethics */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-6">Our Commitment to Ethics</h2>
          <p className="text-xl leading-relaxed text-emerald-50">
            We operate in accordance with good business ethics and the law. We act responsibly and comply with domestic and international legislative frameworks of the petroleum industry. We prefer to do the right thing in good faith and in terms of the law and business ethics.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;